import createStore, { State, StateCreator } from 'zustand';
import { persist, devtools, PersistOptions } from 'zustand/middleware';
import { omit as _omit } from 'lodash';
import { zustandReplayMiddleware } from '.';
import { DeepPartial } from 'lib/types';
import { immer } from './immer';

export interface PersistedStoreOptions<S> {
  persist?: Omit<PersistOptions<S>, 'name'> & { omit?: string[] };
}

const partializePersist = <S extends Object>(state: S, omit?: string[]): DeepPartial<S> => {
  if (!omit) return state;

  // NOTE: According to an article I found, the `omit` method is going to removed in Lodash v5.
  // However that article was published in April 2020, and Lodash is still in v4, so we may
  // need to consider an alternative solution when/if we upgrade to v5.
  // SEE: https://levelup.gitconnected.com/omit-is-being-removed-in-lodash-5-c1db1de61eaf
  const partialState = _omit(state, omit);

  return partialState;
};

export function persistedStore<S extends State>(
  name: string,
  creator: StateCreator<S>,
  options?: PersistedStoreOptions<S>
) {
  return createStore<S>(
    immer(
      zustandReplayMiddleware(
        devtools(
          persist(creator, {
            name,
            partialize: state => partializePersist<S>(state, options?.persist?.omit),
            ...options?.persist
          }),
          {
            name
          }
        ),
        {
          name
        }
      ) as any // Note: would be nice to figure out these types
    )
  );
}
